import { PButton } from '@porsche-design-system/components-react'
import clsx from 'clsx'
import de from 'date-fns/locale/de'
import React from 'react'
import DatePicker, { registerLocale, CalendarContainer } from 'react-datepicker'

import { ReportingContext } from '../../../utils/Context/ReportingContext'
import useOnClickOutside from '../../../utils/ReusableHooks/useOnClickOutside'
import 'react-datepicker/dist/react-datepicker.css'

import { PortalContext } from '../../../utils/PortalContext'

import styles from './calendar.module.css'

registerLocale('de', de)

const Calendar = () => {
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setDatePeriod,
    setFilter,
    filter,
    setOpenDepartmentMenu,
    setOpenApplicationMenu,
    calendarIsOpen,
    setCalendarIsOpen
  } = React.useContext(ReportingContext)

  const { loading } = React.useContext(PortalContext)

  const ref = React.useRef(null)

  const handleClickOutside = () => {
    setCalendarIsOpen(false)
  }

  useOnClickOutside(ref, handleClickOutside)

  const onChange = (dates: any) => {
    const [start, end] = dates
    if (start !== null) {
      setStartDate(start)
      setEndDate(null)
    }
    if (end !== null) {
      setEndDate(new Date(end.setHours(23, 59, 59, 999)))
    }
    if (start && end) {
      const difference = new Date(end).getTime() - new Date(start).getTime()
      const diffInDays = Math.round(difference / (1000 * 3600 * 24))
      setDatePeriod(diffInDays)
      return setCalendarIsOpen(false)
    }
  }

  function getRightFormatForDate(date: Date | null) {
    if (date !== null) {
      return ('0' + date.getDate()).slice(-2) + '.' + ('0' + (date.getMonth() + 1)).slice(-2) + '.' + date.getFullYear()
    }
  }

  const openCalendar = () => {
    setCalendarIsOpen(true)
    setOpenApplicationMenu(false)
    setOpenDepartmentMenu(false)
  }

  const ButtonInput = React.forwardRef((props, ref) => (
    // Improvement necessary, can't fix the issue for missing a property in Buttoninput
    // @ts-ignore
    <PButton ref={ref} variant='tertiary' {...props} onClick={openCalendar} className={styles.button}>
      {getRightFormatForDate(startDate)}
      {endDate !== null && ' - ' + getRightFormatForDate(endDate)}
    </PButton>
  ))

  const MyContainer = ({ className, children }: { className: string; children: React.ReactNode[] }) => {
    return (
      <CalendarContainer className={clsx(className, styles.container)}>
        <div className={styles.calenderWrapper}>{children}</div>
      </CalendarContainer>
    )
  }

  const setLastXDays = (days: number) => {
    const firstDay = new Date(new Date(new Date().setDate(new Date().getDate() - days)).setHours(0, 0, 0, 0))
    const lastDay = new Date(new Date(new Date().setDate(new Date().getDate())).setHours(23, 59, 59, 999))
    setStartDate(firstDay)
    setEndDate(lastDay)
    setDatePeriod(days)
    setCalendarIsOpen(false)
  }

  const setThisMonth = () => {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    const difference = new Date(lastDay).getTime() - new Date(firstDay).getTime()
    const diffInDays = Math.round(difference / (1000 * 3600 * 24))
    setDatePeriod(diffInDays)
    setStartDate(firstDay)
    setEndDate(lastDay)
    setCalendarIsOpen(false)
  }

  const setLastMonth = () => {
    const today = new Date()
    const firstDayOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
    const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0)
    const difference = new Date(lastDayOfLastMonth).getTime() - new Date(firstDayOfLastMonth).getTime()
    const diffInDays = Math.round(difference / (1000 * 3600 * 24))
    setDatePeriod(diffInDays)
    setStartDate(firstDayOfLastMonth)
    setEndDate(lastDayOfLastMonth)
    setCalendarIsOpen(false)
  }

  const setThisYear = () => {
    const firstDayOfCurrentYear = new Date(new Date().getFullYear(), 0, 1)
    const lastDayOfCurrentYear = new Date(new Date().getFullYear(), 11, 31)
    const difference = new Date(lastDayOfCurrentYear).getTime() - new Date(firstDayOfCurrentYear).getTime()
    const diffInDays = Math.round(difference / (1000 * 3600 * 24))
    setDatePeriod(diffInDays)
    setStartDate(firstDayOfCurrentYear)
    setEndDate(lastDayOfCurrentYear)
    setCalendarIsOpen(false)
  }

  const setLastYear = () => {
    const firstDayOfLastYear = new Date(new Date().getFullYear() - 1, 0, 1)
    const lastDayOfLastYear = new Date(new Date().getFullYear() - 1, 11, 31)
    const difference = new Date(lastDayOfLastYear).getTime() - new Date(firstDayOfLastYear).getTime()
    const diffInDays = Math.round(difference / (1000 * 3600 * 24))
    setDatePeriod(diffInDays)
    setStartDate(firstDayOfLastYear)
    setEndDate(lastDayOfLastYear)
    setCalendarIsOpen(false)
  }

  const setThisQuarter = () => {
    const today = new Date()
    const quarter = Math.floor(today.getMonth() / 3)
    const firstDate = new Date(today.getFullYear(), quarter * 3, 1)
    const endDate = new Date(firstDate.getFullYear(), firstDate.getMonth() + 3, 0)
    const difference = new Date(endDate).getTime() - new Date(firstDate).getTime()
    const diffInDays = Math.round(difference / (1000 * 3600 * 24))
    setDatePeriod(diffInDays)
    setStartDate(firstDate)
    setEndDate(endDate)
    setCalendarIsOpen(false)
  }

  const setLastQuarter = () => {
    const today = new Date()
    const quarter = Math.floor(today.getMonth() / 3)
    const firstDate = new Date(today.getFullYear(), quarter * 3 - 3, 1)
    const endDate = new Date(firstDate.getFullYear(), firstDate.getMonth() + 3, 0)
    const difference = new Date(endDate).getTime() - new Date(firstDate).getTime()
    const diffInDays = Math.round(difference / (1000 * 3600 * 24))
    setDatePeriod(diffInDays)
    setStartDate(firstDate)
    setEndDate(endDate)
    setCalendarIsOpen(false)
  }

  React.useEffect(() => {
    if (startDate && endDate) {
      setFilter({
        ...filter,
        from: startDate.getTime(),
        to: endDate.getTime()
      })
    }
  }, [startDate, endDate])

  return (
    <div ref={ref}>
      <DatePicker
        locale='de'
        selected={startDate}
        onChange={onChange}
        dateFormat='dd/MM/yyyy'
        startDate={startDate}
        endDate={endDate}
        selectsRange
        shouldCloseOnSelect={false}
        monthsShown={2}
        customInput={<ButtonInput />}
        open={calendarIsOpen}
        disabled={loading}
        calendarContainer={MyContainer}
      >
        <div className={styles.timeFilter}>
          <button onClick={() => setLastXDays(30)}>Letzte 30 Tage</button>
          <button onClick={setThisMonth}>Dieser Monat</button>
          <button onClick={setLastMonth}>Letzter Monat</button>
          <button onClick={() => setLastXDays(90)}>Letzte 90 Tage</button>
          <button onClick={setThisQuarter}>Dieses Quartal</button>
          <button onClick={setLastQuarter}>Letztes Quartal</button>
          <button onClick={() => setLastXDays(365)}>Letzte 365 Tage</button>
          <button onClick={setThisYear}>Dieses Jahr</button>
          <button onClick={setLastYear}>Letztes Jahr</button>
        </div>
      </DatePicker>
    </div>
  )
}

export default Calendar
